/**
 * Read layer over the spine (`/v2/stats/timeseries|aggregate|correlation|calendar|
 * export`). All accept an optional `clientId` (trainer/org with consent — 403 is
 * an EXPECTED state, mapped by ClientStatsGuard). Values are in canonical
 * units — display via `utils/metricUnits.ts`.
 */
import type {
  FetchOrgWorkExportArgs,
  FetchCorrelationArgs,
  FetchTimeseriesArgs,
  FetchAggregateArgs,
  FetchCalendarArgs,
  FetchExportArgs,
  ApiCorrelation,
  ApiTimeseries,
  ApiAggregate,
  ApiCalendar,
} from './types/stats.types';

import { get } from './axios';

export const fetchTimeseries = async (args: FetchTimeseriesArgs) => {
  const response = await get<ApiTimeseries>('/v2/stats/timeseries', {
    query: {
      category: args.category,
      clientId: args.clientId,
      fn: args.fn,
      from: args.from,
      granularity: args.granularity,
      includeOptimum: args.includeOptimum ? 'true' : undefined,
      metricKeys: args.metricKeys.join(','),
      source: args.source,
      to: args.to,
    },
  });
  return response ?? null;
};

function aggregateQuery(args: FetchAggregateArgs) {
  return {
    category: args.category,
    clientId: args.clientId,
    fns: args.fns?.join(','),
    from: args.from,
    metricKeys: args.metricKeys.join(','),
    // Compare up to 3 periods — BE receives a JSON array of {from,to}
    ranges: args.ranges ? JSON.stringify(args.ranges) : undefined,
    source: args.source,
    to: args.to,
  };
}

export const fetchAggregate = async (args: FetchAggregateArgs) => {
  const response = await get<ApiAggregate>('/v2/stats/aggregate', {
    query: aggregateQuery(args),
  });
  return response ?? null;
};

/**
 * Binary download (xlsx/csv) — called imperatively from a handler (not via
 * React Query): same parameters as aggregate + `format`.
 */
export const fetchStatsExport = async ({
  format,
  ...args
}: FetchExportArgs) => {
  const response = await get<Blob>('/v2/stats/export', {
    query: { ...aggregateQuery(args), format },
    responseType: 'blob',
  });
  return response ?? null;
};

/**
 * F11 — org work-metrics export (CSV/XLSX): session-level data (long/tidy
 * format) for the caller's organization. Same blob-download shape as
 * `fetchStatsExport` above, called imperatively from a handler.
 */
export const fetchOrgWorkExport = async ({
  format,
  from,
  organizationId,
  to,
}: FetchOrgWorkExportArgs) => {
  const response = await get<Blob>(
    `/v2/organizations/${organizationId}/stats/export`,
    {
      query: { format, from, to },
      responseType: 'blob',
    },
  );
  return response ?? null;
};

export const fetchCorrelation = async (args: FetchCorrelationArgs) => {
  const response = await get<ApiCorrelation>('/v2/stats/correlation', {
    query: {
      clientId: args.clientId,
      from: args.from,
      metricKeys: args.metricKeys.join(','),
      to: args.to,
    },
  });
  return response ?? null;
};

export const fetchStatsCalendar = async (args: FetchCalendarArgs) => {
  const response = await get<ApiCalendar>('/v2/stats/calendar', {
    query: {
      clientId: args.clientId,
      metricKeys: args.metricKeys?.length
        ? args.metricKeys.join(',')
        : undefined,
      month: args.month,
    },
  });
  return response ?? null;
};
